import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Box, CircularProgress, IconButton } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Dropzone from 'react-dropzone';
import {
  getVideoFileUploadHeader,
  startVideoFileUpload,
} from '../../utils/bunny-cdn.util';
import { validateSelectedVideoFile } from '../../utils/file-validations.util';
import Player from '../common/player.component';
import {
  downloadVideoAsset,
  getPlayableStream,
} from '../../services/content.service';
import Iconify from '../common/iconify.component';

const VideoUpload = ({
  dataId,
  dataTitle,
  showToastMsg,
  videoData,
  videoContentType,
}) => {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFilesObj, setSelectedFilesObj] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const handleDownloadFile = async () => {
    const paramsQuery = '?type=VIDEO';
    downloadVideoAsset(dataId, paramsQuery)
      .then((res) => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);

        let fileName = '';
        const disposition = res.headers['content-disposition'];
        if (disposition) {
          const match = disposition.match(/filename="?(.+?)"?$/);
          if (match) {
            [, fileName] = match;
          }
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      })
      .catch((e) => {
        showToastMsg('error', e.response?.data?.message || 'Download failed');
      });
  };

  const progressBar = (progressPercentage) => {
    setProgress(progressPercentage);
  };

  const handleSelectFiles = async (files) => {
    showToastMsg();
    setSelectedFilesObj(files);
    setProgress(0);

    const validationError = validateSelectedVideoFile(files);
    if (validationError) {
      showToastMsg('error', validationError);
    }

    // Show file name below button
    for (let i = 0; i < files.length; i += 1) {
      setSelectedFileName(files[i].name);
    }
  };

  const handleUploadFiles = async (videoId, videoTitle) => {
    showToastMsg();

    const validationError = validateSelectedVideoFile(selectedFilesObj);
    if (validationError) {
      showToastMsg('error', validationError);
    } else {
      setShowProgressBar(true);
      try {
        const fileUploadHeader = await getVideoFileUploadHeader(
          videoId,
          videoTitle,
          videoContentType
        );

        if (fileUploadHeader && fileUploadHeader.guid !== '') {
          startVideoFileUpload(
            selectedFilesObj[0],
            fileUploadHeader,
            videoTitle,
            progressBar,
            showToastMsg
          );
        }
      } catch {
        showToastMsg('error', 'Something went wrong! Please try again.');
      }
    }
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const LinearProgressWithLabel = (props) => {
    const { value } = props;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{`${Math.round(
            value
          )}%`}</Typography>
        </Box>
      </Box>
    );
  };

  LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
  };

  useEffect(() => {
    let uri = `videoID=${dataId}`;
    uri += `&videoContentType=${videoContentType}`;
    uri += `&upload=true`;

    getPlayableStream(uri)
      .then((res) => {
        setVideoUrl(res.data?.videoUrl || '');
      })
      .catch(() =>
        setTimeout(() => {
          setVideoUrl('');
        }, 5000)
      );
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sm={4}
        md={4}
        sx={{
          whiteSpace: 'unset',
          wordBreak: 'break-all',
        }}
      >
        <Typography variant="button" display="block">
          {videoContentType === 'videos' ? 'Video' : 'Ad'} Preview:
        </Typography>
        <Box>
          {videoUrl && (
            <Player
              videoUrl={videoUrl}
              height="135px"
              width="240px"
              autoplay={false}
            />
          )}
        </Box>

        <Typography variant="button" display="block" mt={1}>
          {videoContentType === 'videos' ? 'Video' : 'Ad'} URL:
          <IconButton
            aria-label="download"
            size="small"
            onClick={() => handleDownloadFile()}
          >
            <Iconify icon="ic:round-download" />
          </IconButton>
        </Typography>
        <Typography variant="caption">{videoData.videoUrl}</Typography>
      </Grid>

      <Grid item xs={12} sm={8} md={8}>
        <Grid item xs={12} sm={12} md={12}>
          <Dropzone
            onDrop={(acceptedFiles) => handleSelectFiles(acceptedFiles)}
            disabled={showProgressBar}
          >
            {({ getRootProps, getInputProps }) => (
              <section>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />

                  <Box
                    component="section"
                    sx={{
                      p: 1,
                      border: '1px dashed grey',
                      borderRadius: '20px',
                      width: '100%',
                      marginTop: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={12}>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Iconify
                            icon="mdi:cloud-upload-outline"
                            width={42}
                            sx={{ color: 'text.secondary' }}
                          />
                          Drag and drop file here, or click the button below
                        </Grid>

                        <Box
                          display="flex"
                          justifyContent="center"
                          color="primary.main"
                          mt={1}
                        >
                          <Typography variant="body2" display="block">
                            {selectedFileName}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="center" p={1}>
                          <Button
                            component="label"
                            variant="contained"
                            sx={{
                              backgroundColor: '#808080',
                            }}
                            size="small"
                            disabled={showProgressBar}
                          >
                            Browse Video
                          </Button>
                        </Box>

                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            Supported formats: MP4, MOV, WMV, FLV, AVI, MKV,
                            WEBM
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {showProgressBar && (
                      <Box sx={{ width: '100%' }} id="progress-bar">
                        <LinearProgressWithLabel value={progress} />
                      </Box>
                    )}
                  </Box>
                </div>
              </section>
            )}
          </Dropzone>
        </Grid>

        <Box display="flex" justifyContent="center" p={1}>
          {!showProgressBar ? (
            <Button
              color="primary"
              variant="contained"
              size="small"
              disabled={progress > 0}
              onClick={() => handleUploadFiles(dataId, dataTitle)}
              startIcon={<Iconify icon="mdi:cloud-upload" />}
            >
              Upload Video
            </Button>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              padding="4px 10px"
              borderRadius="6px"
              sx={{
                backgroundColor: '#919eab3d',
                color: '#919eabcc',
              }}
              fontSize={14}
            >
              <CircularProgress
                disableShrink
                size="14px"
                sx={{ marginRight: 1, color: '#919eabcc' }}
              />
              <span>Uploading...</span>
            </Box>
          )}
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="caption">
            <b>Note:</b>&nbsp;It might take time to play/preview as per uploaded
            video duration and size.
          </Typography>
          <Typography variant="caption">
            Do not close or refresh browser while video is uploading.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

VideoUpload.propTypes = {
  dataId: PropTypes.number.isRequired,
  dataTitle: PropTypes.string.isRequired,
  showToastMsg: PropTypes.func.isRequired,
  videoData: PropTypes.object.isRequired,
  videoContentType: PropTypes.string.isRequired,
};

export default VideoUpload;
